import { ShiftAction } from 'components/RenderShiftActions/RenderShiftActions.types';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import { useTranslation } from 'hooks/useTranslation';
import { SetStateAction, MouseEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import schedulingReadAccessFacilityIdsSelector from 'store/selectors/scheduleSelectors/schedulingReadAccessFacilityIdsSelector';
import { ActivityEventEnum, OrderStatusEnum, Order, Review } from 'types/graphql';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { getShiftBiddingStatus } from 'utils/shiftStatus';
import { getShiftStartTimeWithInOneHour } from 'utils/time';

export type CallOutShiftStatus = 'IN_PROGRESS' | 'PAUSED' | 'COMPLETE';

type BuildActionsForShiftCardParams = {
  order:
    | Order
    | (Order & {
        review?: Review | null;
      });
  setIsContinueCallOutConfirmationOpen: (value: SetStateAction<boolean>) => void;
  setIsAllowOvertimeConfirmationOpen: (value: SetStateAction<boolean>) => void;
  setIsAutoRebidConfirmationOpen: (value: SetStateAction<boolean>) => void;
  goToAwardTeamMemberFlow: (e: MouseEvent) => void;
  openSendToAgenciesFlow: (e: MouseEvent) => void;
};

type BuildActionsForShiftCardResult = {
  shiftActions: ShiftAction[];
  isShiftInfoWrapperVisible: boolean;
  callOutStatus: CallOutShiftStatus;
};

function createShiftAction(label: string, action: (e: unknown) => void): ShiftAction {
  return {
    label,
    action,
  };
}

export function useBuildActionsForShiftCard({
  order,
  setIsContinueCallOutConfirmationOpen,
  setIsAllowOvertimeConfirmationOpen,
  setIsAutoRebidConfirmationOpen,
  goToAwardTeamMemberFlow,
  openSendToAgenciesFlow,
}: BuildActionsForShiftCardParams): BuildActionsForShiftCardResult {
  const {
    allowOvertime,
    canSendToAgency,
    community,
    currentlyActiveCalloutList,
    isReviewAllowed,
    recurrence,
    review,
    sentToAgencies,
    status,
    biddable,
    bidders,
    biddingTimeEndsAt,
    autoRebid,
    startAt,
    activities,
  } = order;
  const { timeZone } = getCurrentlySelectedLocation();
  const isShiftStartTimeWithinOneHour = getShiftStartTimeWithInOneHour(startAt, timeZone);

  const { t } = useTranslation();

  const isReadOnly = useSelector(
    FEATURE_SELECTORS_MAP['FEATURE_TYPE_YMCA_READ_ONLY_SECONDARY_USER'],
  );

  const locationsWithScheduling = useSelector(schedulingReadAccessFacilityIdsSelector);
  const isSchedulingEnabled = useMemo(
    () => locationsWithScheduling.includes(Number(community.id)),
    [community.id, locationsWithScheduling],
  );

  const isCalloutPaused = useMemo(
    () =>
      currentlyActiveCalloutList?.waitingForManualApproval &&
      !currentlyActiveCalloutList?.isCompleted &&
      status.value === OrderStatusEnum.pending,
    [
      currentlyActiveCalloutList?.isCompleted,
      currentlyActiveCalloutList?.waitingForManualApproval,
      status.value,
    ],
  );

  const isCalloutComplete = useMemo(
    () => currentlyActiveCalloutList?.isCompleted && status.value === OrderStatusEnum.pending,
    [currentlyActiveCalloutList?.isCompleted, status.value],
  );

  const isAllowOvertimeMenuItem =
    !recurrence &&
    !allowOvertime &&
    !biddable &&
    status.value === OrderStatusEnum.pending &&
    isSchedulingEnabled;

  const biddingStatus = getShiftBiddingStatus({
    status: status.value,
    biddable: biddable,
    biddingTimeEndsAt: biddingTimeEndsAt,
    bidders: bidders,
  });

  const isAutoBidAllowed =
    !isShiftStartTimeWithinOneHour &&
    biddable &&
    !autoRebid &&
    !sentToAgencies &&
    bidders.length === 0 &&
    (biddingStatus === 'NO_BIDS' || biddingStatus === 'BIDDING');

  const isShiftAllowedToSendToAgency = useMemo((): boolean => {
    const canSendShiftToAgency = !!canSendToAgency && !sentToAgencies;

    if (biddable) {
      const isShiftBiddingPeriodEnd = activities?.some(
        (activity) => activity.event.name === ActivityEventEnum.bidding_end,
      );
      return canSendShiftToAgency && bidders.length === 0 && isShiftBiddingPeriodEnd;
    }

    return canSendShiftToAgency;
  }, [biddable, activities, canSendToAgency, sentToAgencies, bidders]);

  const isShiftInfoWrapperVisible =
    !isReadOnly &&
    (!!isReviewAllowed ||
      !!canSendToAgency ||
      !!isCalloutPaused ||
      !!isAllowOvertimeMenuItem ||
      !!review?.rating ||
      biddingStatus === 'AWARDING' ||
      isAutoBidAllowed);

  const actions: ShiftAction[] = [];
  if (!!isCalloutPaused)
    actions.push(
      createShiftAction(t('dashboard:CONTINUE_CALL_OUT'), () =>
        setIsContinueCallOutConfirmationOpen(true),
      ),
    );
  if (!!isAutoBidAllowed)
    actions.push(
      createShiftAction(t('create_shifts:AUTO_EXTEND_BID'), () =>
        setIsAutoRebidConfirmationOpen(true),
      ),
    );
  if (isShiftAllowedToSendToAgency)
    actions.push(
      createShiftAction(
        t('schedule:SEND_TO_AGENCY'),
        openSendToAgenciesFlow as (e: unknown) => void,
      ),
    );
  if (biddingStatus === 'AWARDING')
    actions.push(
      createShiftAction(
        t('dashboard:AWARD_SHIFT'),
        goToAwardTeamMemberFlow as (e: unknown) => void,
      ),
    );
  if (!!isAllowOvertimeMenuItem)
    actions.push(
      createShiftAction(t('dashboard:ALLOW_OVERTIME'), () =>
        setIsAllowOvertimeConfirmationOpen(true),
      ),
    );
  return {
    shiftActions: actions,
    isShiftInfoWrapperVisible,
    callOutStatus: isCalloutPaused ? 'PAUSED' : isCalloutComplete ? 'COMPLETE' : 'IN_PROGRESS',
  };
}
